import { Box, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { colors } from '@src/styles';
import { CanonicalItemDTO } from '@src/services/automations/modules/v1/canonicals/dto/canonicals.get.response.dto';
import { formatCanonicalDate, monoFontFamily, titleFontFamily, tonePillSX } from './canonicalStyles';

export interface CanonicalCardProps {
  canonical: CanonicalItemDTO;
  onCanonicalClick: (canonical: CanonicalItemDTO) => void;
}

export const CanonicalCard = ({ canonical, onCanonicalClick }: CanonicalCardProps) => {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onCanonicalClick(canonical)}
      sx={{
        display: 'flex',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        alignItems: 'center',
        columnGap: 2,
        rowGap: 0.5,
        width: '100%',
        minWidth: 0,
        minHeight: 52,
        padding: '8px 14px',
        textAlign: 'left',
        cursor: 'pointer',
        backgroundColor: colors.brand.surface,
        border: `1px solid ${colors.brand.line}`,
        borderRadius: '10px',
        fontFamily: titleFontFamily,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': { borderColor: colors.primary.yellow, backgroundColor: '#141A1F' },
        '&:hover .canonical-card-arrow': { transform: 'translateX(3px)', color: colors.primary.yellow },
        '&:focus-visible': { outline: `2px solid ${colors.primary.yellow}`, outlineOffset: '2px' },
      }}
    >
      <Typography
        title={canonical.key}
        sx={{
          flex: { xs: '1 1 auto', md: '0 0 300px' },
          minWidth: 0,
          fontFamily: monoFontFamily,
          fontSize: '13px',
          fontWeight: 500,
          color: colors.primary.white,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {canonical.key}
      </Typography>

      <Typography
        title={canonical.description || undefined}
        sx={{
          order: { xs: 3, md: 0 },
          flex: { xs: '1 1 100%', md: '1 1 auto' },
          minWidth: 0,
          fontSize: '12.5px',
          color: canonical.description ? colors.brand.textSecondary : colors.brand.textMuted,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {canonical.description || 'Sem descrição'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        {canonical.resourceId && <Box sx={tonePillSX(colors.primary.blue)}>Schema</Box>}
        {canonical.tarkenExtensionId && <Box sx={tonePillSX(colors.status.concluded)}>Extension</Box>}
      </Box>

      <Typography
        sx={{
          display: { xs: 'none', sm: 'block' },
          flexShrink: 0,
          minWidth: 170,
          whiteSpace: 'nowrap',
          textAlign: 'right',
          fontFamily: monoFontFamily,
          fontSize: '10.5px',
          letterSpacing: '0.04em',
          color: colors.brand.textMuted,
        }}
      >
        {formatCanonicalDate(canonical.updatedAt || canonical.createdAt)}
      </Typography>

      <ArrowForwardIcon
        className="canonical-card-arrow"
        sx={{
          flexShrink: 0,
          fontSize: 16,
          color: colors.brand.textMuted,
          transition: 'transform 0.18s ease, color 0.18s ease',
        }}
      />
    </Box>
  );
};
